import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { messaggioDi } from "@/lib/messaggio-errore";
import { giornoOra } from "@/lib/crm";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

/**
 * Claude collegato (c37, D12): i collegamenti di chi guarda, con «Scollega». Il titolare li vede tutti e
 * legge le proposte che Claude gli ha lasciato. «Scollega» stacca Claude subito: lato server revoca il
 * collegamento e cancella il client OAuth, così Claude non può riapprovarsi da solo.
 */

const DIECI_MINUTI = 10 * 60 * 1000;

/** Come sta un collegamento, detto in parole. */
export function statoCollegamento(r, adesso = Date.now()) {
  if (r.revocato_at) return { chiave: "scollegato", testo: `Scollegato il ${giornoOra(r.revocato_at)}` };
  if (r.legato_at) return { chiave: "attivo", testo: `Attivo, usato la prima volta il ${giornoOra(r.legato_at)}` };
  if (adesso - new Date(r.creato_at).getTime() > DIECI_MINUTI)
    return { chiave: "scaduto", testo: "Non completato: Claude non ha fatto la prima domanda in tempo. Scollegalo e ricollega." };
  return { chiave: "attesa", testo: "In attesa della prima domanda a Claude" };
}

export default function ClaudeCollegatoTab() {
  const { profile } = useAuth();
  const isTitolare = profile?.ruolo === "titolare";
  const [righe, setRighe] = useState(null);
  const [proposte, setProposte] = useState([]);
  const [errore, setErrore] = useState("");
  const [sicuro, setSicuro] = useState(null); // l'id del collegamento a cui si è premuto una volta «Scollega»
  const [invio, setInvio] = useState(false);

  const carica = useCallback(async () => {
    try {
      setErrore("");
      setRighe((await api.get("/mcp/collegamenti")).data.righe);
      if (isTitolare) setProposte((await api.get("/mcp/proposte")).data.righe);
    } catch (e) {
      setErrore(messaggioDi(e));
      setRighe([]);
    }
  }, [isTitolare]);

  useEffect(() => {
    carica();
  }, [carica]);

  const scollega = async (id) => {
    setInvio(true);
    try {
      const r = await api.delete(`/mcp/collegamenti/${id}`);
      if (r.data?.accesso_cancellato === false) toast.warning("Claude è scollegato, ma non sono riuscito a chiudere l'accesso su Supabase: riprova fra poco.");
      else toast.success("Claude è scollegato");
      setSicuro(null);
      await carica();
    } catch (e) {
      toast.error(messaggioDi(e));
    } finally {
      setInvio(false);
    }
  };

  return (
    <div className="space-y-card" data-testid="claude-collegato">
      <Card className="space-y-inside p-card">
        <h2 className="text-base font-semibold text-foreground">Claude collegato</h2>
        <p className="text-sm text-muted-foreground">
          Claude lavora nel gestionale con i tuoi permessi: legge, aggiunge note e prepara bozze che rileggi tu. Non invia, non
          firma, non cancella e non vede telefoni, email, indirizzi, costi né margini. Qui lo scolleghi quando vuoi.
        </p>
        <p className="text-sm text-muted-foreground">
          Se Claude smette di rispondere dopo che hai rifatto l&#39;accesso con il codice di verifica, scollegalo da qui e collegalo di nuovo: quell&#39;accesso
          chiude gli altri collegamenti.
        </p>
      </Card>

      {errore ? (
        <p role="alert" className="text-sm text-danger" data-testid="claude-errore">
          {errore}
        </p>
      ) : null}

      {righe === null ? (
        <Skeleton className="h-24 w-full" />
      ) : righe.length === 0 && !errore ? (
        <Card className="p-card" data-testid="claude-nessuno">
          <p className="text-sm text-muted-foreground">
            Nessun Claude collegato. Lo aggiungi da Claude, come connettore personalizzato: la guida «Collegare Claude al
            gestionale» ti accompagna passo per passo.
          </p>
        </Card>
      ) : (
        <ul className="space-y-inside" data-testid="claude-elenco">
          {righe.map((r) => {
            const s = statoCollegamento(r);
            return (
              <li key={r.id}>
                <Card className="flex flex-col gap-inside p-card sm:flex-row sm:items-center sm:justify-between" data-testid={`claude-riga-${s.chiave}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {r.client_nome}
                      {isTitolare && !r.mio ? <span className="font-normal text-muted-foreground"> · di {r.persona || "un collega"}</span> : null}
                    </p>
                    <p className="text-sm text-muted-foreground">{s.testo}</p>
                  </div>
                  {s.chiave !== "scollegato" ? (
                    sicuro === r.id ? (
                      <div className="flex gap-inside">
                        <Button variant="outline" size="sm" onClick={() => setSicuro(null)} disabled={invio}>
                          Annulla
                        </Button>
                        <Button size="sm" onClick={() => scollega(r.id)} disabled={invio} data-testid="claude-scollega-conferma">
                          Sì, scollega
                        </Button>
                      </div>
                    ) : (
                      <Button variant="outline" size="sm" onClick={() => setSicuro(r.id)} data-testid="claude-scollega">
                        Scollega
                      </Button>
                    )
                  ) : null}
                </Card>
              </li>
            );
          })}
        </ul>
      )}

      {isTitolare ? (
        <Card className="space-y-inside p-card" data-testid="claude-proposte">
          <h2 className="text-base font-semibold text-foreground">Proposte di Claude</h2>
          {proposte.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna proposta, per ora. Quando Claude ha un&#39;idea per migliorare il gestionale la trovi qui.</p>
          ) : (
            <ul className="space-y-inside">
              {proposte.map((p) => (
                <li key={p.id} className="text-sm">
                  <span className="text-muted-foreground">
                    {giornoOra(p.creato_at)} · {p.persona || "un collega"}:{" "}
                  </span>
                  <span className="whitespace-pre-wrap text-foreground">{p.testo}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </div>
  );
}
